import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import type { ButtonProps } from '@mui/material';

export interface ButtonComponentProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loadingText?: string;
  sx?: ButtonProps['sx'];
  href?: string;
  target?: string;
  rel?: string;
}

/**
 * ButtonComponent - Reusable Button Component
 * 
 * A standardized button component that provides:
 * - Consistent styling and behavior across the application
 * - Built-in loading state with spinner
 * - Support for various button variants and colors
 * - Icon support (start and end icons)
 * - Link functionality when href is provided
 * - Flexible sizing and layout options
 */
export const ButtonComponent: React.FC<ButtonComponentProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  loading = false,
  startIcon,
  endIcon,
  loadingText,
  sx,
  href,
  target,
  rel,
}) => {
  const isDisabled = disabled || loading;
  
  const getStartIcon = () => {
    if (loading) {
      return <CircularProgress size={16} color="inherit" />;
    }
    return startIcon;
  };

  const getEndIcon = () => {
    if (loading && !startIcon) {
      return <CircularProgress size={16} color="inherit" />;
    }
    return endIcon;
  };

  const buttonContent = loading && loadingText ? loadingText : children;

  const commonProps = {
    variant,
    color,
    size,
    disabled: isDisabled,
    fullWidth,
    startIcon: getStartIcon(),
    endIcon: getEndIcon(),
    sx: {
      fontWeight: 500,            // font-medium
      borderRadius: "0.5rem",     // rounded-lg
      fontSize: "0.875rem",       // text-sm
      padding: "0.625rem 1.25rem",// py-2.5 px-5
      boxShadow: "none",
      transition: 'all 0.2s ease-in-out',
      textTransform: 'none',
      ...sx,
    } as const,
  };

  if (href) {
    return (
      <Button
        {...commonProps}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
      >
        {buttonContent}
      </Button>
    );
  }

  return (
    <Button
      {...commonProps}
      type={type}
      onClick={onClick}
    >
      {buttonContent}
    </Button>
  );
};

export default ButtonComponent;